import { useState, useEffect } from "react";
import { Users } from "lucide-react";
import { clubService } from "@/lib/services/organization";
import { CLUB_TYPE_LABELS } from "@/types";
import type { Club, ClubType } from "@/types";

/**
 * Muestra un banner cuando el usuario llega por invitación de un club.
 * Lee el invite code de sessionStorage (guardado por InviteCapture en App.tsx).
 */
export default function InviteBanner() {
  const [club, setClub] = useState<Club | null>(null);

  useEffect(() => {
    const code =
      sessionStorage.getItem("pending_invite") ||
      localStorage.getItem("pending_invite");
    if (!code) return;
    clubService
      .getByInviteCode(code)
      .then((c) => setClub(c))
      .catch((err) => {
        // No interrumpe UX: el banner es opcional. Solo loguea para diagnóstico.
        if (import.meta.env.DEV) {
          console.warn("[InviteBanner] no se pudo resolver invite code:", err);
        }
      });
  }, []);

  if (!club) return null;

  // El club puede tener 'type' (legacy) o 'sections' con múltiples tipos
  const primaryType: ClubType | undefined =
    club.type ??
    (club.sections ? (Object.keys(club.sections) as ClubType[])[0] : undefined);
  const typeLabel = primaryType
    ? (CLUB_TYPE_LABELS[primaryType] ?? "Club")
    : "Club";

  return (
    <div className="mb-6 p-4 rounded-2xl bg-gradient-to-r from-primary-50 to-accent-50 border-2 border-primary-100 flex items-start gap-3">
      {club.logoUrl ? (
        <img
          src={club.logoUrl}
          alt={club.name}
          className="w-10 h-10 rounded-xl object-cover shrink-0 border border-primary-100"
        />
      ) : (
        <div className="w-9 h-9 bg-gradient-to-br from-primary-500 to-accent-600 rounded-xl flex items-center justify-center flex-shrink-0 shadow-sm">
          <Users className="w-4 h-4 text-white" />
        </div>
      )}
      <div className="min-w-0">
        <p className="text-sm font-semibold text-primary-800">
          ¡Fuiste invitado a unirte!
        </p>
        <p className="text-sm text-primary-700 truncate">
          <span className="font-bold">{club.name}</span>
          <span className="text-primary-500 mx-1">·</span>
          <span className="text-primary-600">{typeLabel}</span>
        </p>
      </div>
    </div>
  );
}
